import { Collection, Database } from '@nozbe/watermelondb';

import Entry from '../../Entry';
import Horse from '../../Horse';
import Organisation from '../../Organisation';
import {
    DBServiceOptionsWithImagesAndValidation,
    DBServiceOptionsWithImagesLoggerAndValidation,
} from '../../../../types/dbService';
import { EntryModel } from '../../../../types/Entries';
import { EntryProcedureModel } from '../../../../types/EntryProcedure';
import { EntryProductModel } from '../../../../types/EntryProduct';
import {
    EVENT_CONTACT_STATUS,
    EventContactModel,
} from '../../../../types/EventContact';
import { EventContactReminderModel } from '../../../../types/EventContactReminder';
import { EventHorseModel } from '../../../../types/EventHorse';
import {
    EventModel,
    EventPayload,
    EventCategory,
    PreparedDataOnAdd,
    EventEntityTableName,
    ContactInvitationsPayload,
} from '../../../../types/Events';
import { HorseModel } from '../../../../types/Horses';
import { InventoryChangeModel } from '../../../../types/InventoryChange';
import { InventoryProductModel } from '../../../../types/InventoryProduct';
import { LocationModel } from '../../../../types/Location';
import { isFuture } from '../../../../utils/date';
import { getTimeZone } from '../../../../utils/timezone';
import { EntryUserModel } from '../../../../types/EntryUser';
import EntryUser from '../../EntryUser';
import EventUser from '../../EventUser';
import { EventUserModel } from '../../../../types/EventUser';
import moment from 'moment';
import { DBWriterLoggerFunction } from '../../../../types/logger';
import { Identificator } from '../../../../types/Identificator';
import {
    addRelatedDataBatch,
    addFirebaseInvitations,
    mapRecords,
    prepareEventLocation,
    schedulePN,
} from './common';
import { UserModel, UserPayload } from '../../../../types/User';
import User from '../../User';

const prepareCreateEvent = async ({
    payload,
    userId,
    serviceOptions,
    locationElement,
    collection,
}: {
    payload: EventPayload;
    userId: string;
    serviceOptions: DBServiceOptionsWithImagesAndValidation;
    locationElement: LocationModel | null;
    collection: Collection<EventModel>;
}) => {
    const now = moment().toISOString();
    const organisationService = new Organisation(serviceOptions);
    const organisation = await organisationService.get();
    const { id: organisationID } = organisation[0];

    const createdEvent = await collection.prepareCreate((event) => {
        event.title = payload.title;
        event.address = payload.address || null;
        event.startsTime = payload.startsTime;
        event.endsTime = payload.endsTime;
        event.notes = payload.notes || null;
        event.userReminder = payload.userReminder;
        event.contactsReminder = payload.contactsReminder;
        event.ownersReminder = payload.ownersReminder;
        event.sendInvitesToContactsAt = now;
        event.locationId = locationElement?.id ? locationElement?.id : null;
        event.userId = userId;
        event.organisationId = organisationID;
        event.timezone = payload.timezone || getTimeZone();
        event.category = payload.category || EventCategory.standard;
        event.isRecurring = payload.isRecurring ?? false;
        event.recurrencePattern = payload.recurrencePattern;
        event.recurrenceEndDate = payload.recurrenceEndDate;
        event.originalEventId = payload.originalEventId;
    });

    return createdEvent;
};

const addHorsesRelatedData = async ({
    createdEvent,
    horses,
    serviceOptions,
    database,
    logDBAction,
    tableName,
    payload,
    userId,
    membersIds,
}: {
    createdEvent: EventModel;
    horses: Identificator[];
    serviceOptions: DBServiceOptionsWithImagesLoggerAndValidation;
    database: Database;
    logDBAction: DBWriterLoggerFunction;
    tableName: EventEntityTableName;
    payload: EventPayload;
    userId: string;
    membersIds?: string[];
}) => {
    let eventHorsesToAdd: EventHorseModel[] = [];
    let horsesToUpdate: HorseModel[] = [];
    let entriesToAdd: EntryModel[] = [];
    let entryProceduresToAdd: EntryProcedureModel[] = [];
    let entryProductsToAdd: EntryProductModel[] = [];
    let inventoryProductsToAdd: InventoryProductModel[] = [];
    let inventoryChanges: InventoryChangeModel[] = [];
    let entryUsersToAdd: EntryUserModel[] = [];

    if (horses?.length && horses.length > 0) {
        const eventHorsesCollection =
            database.collections.get<EventHorseModel>('event_horses');

        for (let horse of horses) {
            const createdEventHorse = await eventHorsesCollection.prepareCreate(
                (eventHorse) => {
                    eventHorse.eventId = createdEvent.id;
                    eventHorse.userId = createdEvent.userId;
                    eventHorse.organisationId = createdEvent.organisationId;
                    eventHorse.horseId = horse.id;
                },
            );

            eventHorsesToAdd.push(createdEventHorse);

            logDBAction({
                message: 'Create event - create horse',
                modelName: tableName,
                payload: createdEventHorse,
            });
        }

        // Add Entries & update badge for each horse
        if (payload.createEntries) {
            const entryService = new Entry(serviceOptions);
            const entryUserService = new EntryUser(serviceOptions);
            const horseService = new Horse(serviceOptions);

            const entryCollection =
                database.collections.get<EntryModel>('entries');

            for (let horse of horses) {
                const lastHorseEntry =
                    await entryService.getNewestEntryByHorseID(horse.id);
                const horseEntity = await horseService.getByID(horse.id);
                let createdEntry: EntryModel;

                if (lastHorseEntry) {
                    createdEntry = await entryCollection.prepareCreate(
                        (entry) => {
                            entry.eventId = createdEvent.id;
                            entry.userId = createdEvent.userId;
                            entry.organisationId = createdEvent.organisationId;
                            entry.loggedTime = createdEvent.startsTime;
                            entry.title = lastHorseEntry.title;
                            entry.notes = lastHorseEntry.notes;
                            entry.privateNotes = lastHorseEntry.privateNotes;
                            entry.horseId = horseEntity.id;
                        },
                    );

                    logDBAction({
                        message:
                            'Create event - create entry (last horse entry)',
                        modelName: tableName,
                        payload: createdEntry,
                    });

                    const entryProducts =
                        await lastHorseEntry.entryProducts.fetch();
                    const entryProductsResults =
                        await entryService.addEntryProducts(
                            createdEntry,
                            entryProducts,
                            userId,
                        );

                    entryProductsToAdd.push(
                        ...entryProductsResults.entryProductsToAdd,
                    );
                    inventoryProductsToAdd.push(
                        ...entryProductsResults.inventoryProductsToAdd,
                    );
                    inventoryChanges.push(
                        ...entryProductsResults.inventoryChanges,
                    );

                    const entryProcedures =
                        await lastHorseEntry.entryProcedures.fetch();
                    const entryProceduresArr =
                        await entryService.addEntryProcedures(
                            createdEntry,
                            entryProcedures,
                        );

                    entryProceduresToAdd.push(...entryProceduresArr);
                } else {
                    createdEntry = await entryCollection.prepareCreate(
                        (entry) => {
                            entry.eventId = createdEvent.id;
                            entry.userId = createdEvent.userId;
                            entry.organisationId = createdEvent.organisationId;
                            entry.loggedTime = createdEvent.startsTime;
                            entry.title = 'service';
                            entry.horseId = horseEntity.id;
                        },
                    );

                    logDBAction({
                        message: 'Create event - create entry',
                        modelName: tableName,
                        payload: createdEntry,
                    });
                }

                entriesToAdd.push(createdEntry);

                // entry members are pre-filled with event members
                if (membersIds) {
                    const entryUsersArr =
                        await entryUserService.prepareAddBatch(
                            membersIds,
                            createdEntry.id,
                        );

                    entryUsersToAdd.push(...entryUsersArr);
                }

                // remove horse badge if the event is in the future
                if (isFuture(createdEvent.startsTime)) {
                    const horseToUpdate = await horseEntity.prepareUpdate(
                        (horse) => {
                            horse.shoeingCycleBadge = false;
                        },
                    );
                    horsesToUpdate.push(horseToUpdate);
                }
            }
        }
    }

    return {
        horsesToUpdate,
        entryProceduresToAdd,
        entryProductsToAdd,
        entryUsersToAdd,
        entriesToAdd,
        eventHorsesToAdd,
        inventoryProductsToAdd,
        inventoryChanges,
    };
};

const addContactsRelatedData = async ({
    createdEvent,
    contacts,
    invitations,
    contactsReminderTimeBefore,
    contactsReminder,
    ownersReminder,
    userReminder,
    database,
    logDBAction,
    tableName,
}: {
    createdEvent: EventModel;
    contacts?: Identificator[];
    invitations?: ContactInvitationsPayload;
    contactsReminderTimeBefore?: string;
    contactsReminder?: boolean;
    ownersReminder?: boolean;
    userReminder?: string | null;
    database: Database;
    logDBAction: DBWriterLoggerFunction;
    tableName: EventEntityTableName;
}) => {
    let eventContactsToAdd: EventContactModel[] = [];
    let eventContactRemindersToAdd: EventContactReminderModel[] = [];

    if (contacts?.length && contacts.length > 0) {
        const eventContactsCollection =
            database.collections.get<EventContactModel>('event_contacts');
        const eventContactRemindersCollection =
            database.collections.get<EventContactReminderModel>(
                'event_contact_reminders',
            );

        await Promise.all(
            contacts.map(async (contact) => {
                const eventContact =
                    await eventContactsCollection.prepareCreate(
                        (eventContact) => {
                            eventContact.eventId = createdEvent.id;
                            eventContact.userId = createdEvent.userId;
                            eventContact.organisationId =
                                createdEvent.organisationId;
                            eventContact.contactId = contact.id;
                            eventContact.sentSms =
                                invitations?.[contact.id].sms ?? false;
                            eventContact.sentEmail =
                                invitations?.[contact.id].email ?? false;
                            eventContact.status = EVENT_CONTACT_STATUS.AWAITING;
                        },
                    );

                eventContactsToAdd.push(eventContact);

                logDBAction({
                    message: 'Create event - create event contact',
                    modelName: tableName,
                    payload: eventContact,
                });

                // userReminder as fallback for feature flag compatibility
                const contactReminderTime =
                    contactsReminderTimeBefore || userReminder;
                const shouldRemindContact = contactsReminder || ownersReminder;

                if (contactReminderTime && shouldRemindContact) {
                    const createdEventContactReminder =
                        await eventContactRemindersCollection.prepareCreate(
                            (eventContactReminder) => {
                                eventContactReminder.eventId = createdEvent.id;
                                eventContactReminder.userId =
                                    createdEvent.userId;
                                eventContactReminder.organisationId =
                                    createdEvent.organisationId;
                                eventContactReminder.timeBefore =
                                    contactReminderTime;
                                eventContactReminder.eventContactId =
                                    eventContact.id;
                            },
                        );

                    eventContactRemindersToAdd.push(
                        createdEventContactReminder,
                    );
                }
            }),
        );
    }

    return {
        eventContactsToAdd,
        eventContactRemindersToAdd,
    };
};

const addEventUsersRelatedData = async ({
    createdEvent,
    membersIds,
    serviceOptions,
}: {
    createdEvent: EventModel;
    membersIds?: string[];
    serviceOptions: DBServiceOptionsWithImagesAndValidation;
}) => {
    let eventUsersToAdd: EventUserModel[] = [];

    if (membersIds?.length) {
        const eventUserService = new EventUser(serviceOptions);
        const eventUsersArr = await eventUserService.prepareAdd(
            membersIds,
            createdEvent.id,
        );
        eventUsersToAdd = [...eventUsersToAdd, ...eventUsersArr];
    }

    return {
        eventUsersToAdd,
    };
};

const addUserRelatedData = async ({
    userId,
    userPayload,
    serviceOptions,
}: {
    userId: string;
    userPayload?: UserPayload;
    serviceOptions: DBServiceOptionsWithImagesAndValidation;
}) => {
    let userToUpdate: UserModel | null = null;

    if (userPayload) {
        const userService = new User(serviceOptions);
        const [user] = await userService.getById(userId);
        userToUpdate = await user.prepareUpdate((user) => {
            for (const key in userPayload) {
                user[key] = userPayload[key];
            }
        });
        return userToUpdate;
    }

    return userToUpdate;
};

const logDBActions = ({
    createdEvent,
    locationElement,
    horsesRelatedData,
    contactsRelatedData,
    eventUsersToAdd,
    logDBAction,
    tableName,
}: {
    createdEvent: EventModel;
    locationElement: LocationModel | null;
    horsesRelatedData: Awaited<ReturnType<typeof addHorsesRelatedData>>;
    contactsRelatedData: Awaited<ReturnType<typeof addContactsRelatedData>>;
    eventUsersToAdd: EventUserModel[];
    logDBAction: DBWriterLoggerFunction;
    tableName: EventEntityTableName;
}) => {
    if (locationElement) {
        logDBAction({
            message: 'Created unique location',
            modelName: tableName,
            payload: locationElement,
        });
    }

    if (createdEvent) {
        logDBAction({
            message: 'Create event',
            modelName: tableName,
            payload: createdEvent,
        });
    }

    logDBAction({
        message: 'Create event - update horses',
        modelName: tableName,
        payload: horsesRelatedData.horsesToUpdate,
    });
    logDBAction({
        message: 'Create event - create entry products',
        modelName: tableName,
        payload: horsesRelatedData.entryProductsToAdd,
    });
    logDBAction({
        message: 'Create event - create entry procedures',
        modelName: tableName,
        payload: horsesRelatedData.entryProceduresToAdd,
    });
    logDBAction({
        message: 'Create event - create entry users',
        modelName: tableName,
        payload: horsesRelatedData.entryUsersToAdd,
    });
    logDBAction({
        message: 'Create event - create event users',
        modelName: tableName,
        payload: eventUsersToAdd,
    });
    logDBAction({
        message: 'Create event - create event contact reminder',
        modelName: tableName,
        payload: contactsRelatedData.eventContactRemindersToAdd,
    });
    logDBAction({
        message: 'Create event - create inventory products',
        modelName: tableName,
        payload: horsesRelatedData.inventoryProductsToAdd,
    });
    logDBAction({
        message: 'Create event - create inventory changes',
        modelName: tableName,
        payload: horsesRelatedData.inventoryChanges,
    });
};

const mapHorsesRelatedData = (
    data: Awaited<ReturnType<typeof addHorsesRelatedData>>,
    preparedData: PreparedDataOnAdd[],
) => {
    const newPreparedData: PreparedDataOnAdd[] = [...preparedData];
    newPreparedData.push(
        ...mapRecords(data.horsesToUpdate, 'horses', 'update'),
    );
    newPreparedData.push(
        ...mapRecords(data.entryProceduresToAdd, 'entry_procedures', 'add'),
    );
    newPreparedData.push(
        ...mapRecords(data.entryProductsToAdd, 'entry_products', 'add'),
    );
    newPreparedData.push(
        ...mapRecords(data.inventoryProductsToAdd, 'inventory_products', 'add'),
    );
    newPreparedData.push(
        ...mapRecords(data.inventoryChanges, 'inventory_changes', 'add'),
    );
    newPreparedData.push(
        ...mapRecords(data.entryUsersToAdd, 'entry_users', 'add'),
    );
    newPreparedData.push(...mapRecords(data.entriesToAdd, 'entries', 'add'));
    newPreparedData.push(
        ...mapRecords(data.eventHorsesToAdd, 'event_horses', 'add'),
    );

    return newPreparedData;
};

const mapContactsRelatedData = (
    data: Awaited<ReturnType<typeof addContactsRelatedData>>,
    preparedData: PreparedDataOnAdd[],
) => {
    const newPreparedData: PreparedDataOnAdd[] = [...preparedData];

    newPreparedData.push(
        ...mapRecords(data.eventContactsToAdd, 'event_contacts', 'add'),
    );
    newPreparedData.push(
        ...mapRecords(
            data.eventContactRemindersToAdd,
            'event_contact_reminders',
            'add',
        ),
    );

    return newPreparedData;
};

const prepareCreateLocation = prepareEventLocation;

export {
    mapHorsesRelatedData,
    mapContactsRelatedData,
    addContactsRelatedData,
    addEventUsersRelatedData,
    addHorsesRelatedData,
    addUserRelatedData,
    addRelatedDataBatch,
    schedulePN,
    logDBActions,
    addFirebaseInvitations,
    prepareCreateEvent,
    prepareCreateLocation,
    mapRecords,
};
